/* A small history router. Pages are plain paths (pinnr.io/r/4821, pinnr.io/@silas); Cloudflare Pages serves
   index.html for all of them. */
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react'

export type RouteName =
  | 'home' | 'setup' | 'room' | 'game' | 'daily' | 'boards' | 'maps' | 'map' | 'mapEdit' | 'mapNew' | 'servers'
  | 'profile' | 'settings' | 'shop' | 'premium' | 'admin' | 'legal' | 'challenge' | 'invite' | 'gift' | 'reset' | 'friends' | 'dev' | 'notfound'
  | 'mini' | 'miniRoom' | 'miniPlay'

export interface Route {
  name: RouteName
  params: Record<string, string>
  path: string
  query: URLSearchParams
}

const TABLE: [RegExp, RouteName, string[]][] = [
  [/^\/$/, 'home', []],
  [/^\/play\/(solo|duo|multi)$/, 'setup', ['kind']],
  [/^\/r\/([A-Za-z0-9]{3,8})$/, 'room', ['code']],
  [/^\/game$/, 'game', []],
  [/^\/daily$/, 'daily', []],
  [/^\/boards(?:\/([a-z0-9:_.-]+))?$/, 'boards', ['id']],
  [/^\/maps$/, 'maps', []],
  [/^\/maps\/new$/, 'mapNew', []],
  [/^\/m\/([A-Za-z0-9_-]{4,24})$/, 'map', ['id']],
  [/^\/m\/([A-Za-z0-9_-]{4,24})\/edit$/, 'mapEdit', ['id']],
  [/^\/servers$/, 'servers', []],
  [/^\/(numbr|splittr)$/i, 'mini', ['game']],
  [/^\/(numbr|splittr)\/r\/([A-Za-z0-9]{4})$/i, 'miniRoom', ['game', 'code']],
  [/^\/(numbr|splittr)\/play$/i, 'miniPlay', ['game']],
  [/^\/@([a-z0-9_.]{1,20})$/, 'profile', ['username']],
  [/^\/friends$/, 'friends', []],
  [/^\/settings$/, 'settings', []],
  [/^\/shop$/, 'shop', []],
  [/^\/premium$/, 'premium', []],
  [/^\/admin(?:\/([a-z-]+))?$/, 'admin', ['section']],
  [/^\/legal(?:\/([a-z-]+))?$/, 'legal', ['doc']],
  [/^\/c\/([A-Za-z0-9_-]{4,24})$/, 'challenge', ['id']],
  [/^\/invite\/([a-z0-9_.]{1,20})$/, 'invite', ['username']],
  [/^\/gift\/([A-Za-z0-9_-]{4,40})$/, 'gift', ['code']],
  [/^\/reset\/([A-Za-z0-9_-]{10,200})$/, 'reset', ['token']],
  [/^\/dev\/([a-z]+)$/, 'dev', ['tool']],
]

function parse(): Route {
  const path = decodeURI(location.pathname).replace(/\/+$/, '') || '/'
  const query = new URLSearchParams(location.search)
  for (const [re, name, keys] of TABLE) {
    const m = re.exec(path)
    if (m) {
      const params: Record<string, string> = {}
      keys.forEach((k, i) => { if (m[i + 1] != null) params[k] = m[i + 1] })
      return { name, params, path, query }
    }
  }
  return { name: 'notfound', params: {}, path, query }
}

let current = parse()
let depth = 0 // pages pushed inside the app this session, so Back never leaves the site
const listeners = new Set<() => void>()
function emit() {
  current = parse()
  listeners.forEach((l) => l())
}

/* Back and Forward land where you were on that page. Each history entry gets a key; scroll positions are kept
   per key, and restored once the page has drawn enough to scroll that far (its data loads after the route). */
const scrolls = new Map<string, number>()
const newKey = () => Math.random().toString(36).slice(2, 10)
let key: string = (history.state as { k?: string } | null)?.k || newKey()
if (!(history.state as { k?: string } | null)?.k) history.replaceState({ k: key }, '')
if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
let saveT = 0
window.addEventListener('scroll', () => {
  if (saveT) return
  saveT = window.setTimeout(() => { saveT = 0; scrolls.set(key, window.scrollY) }, 120)
}, { passive: true })
let restoring = 0
function restore(y: number) {
  const id = ++restoring
  const t0 = performance.now()
  const step = () => {
    if (id !== restoring) return
    const room = document.documentElement.scrollHeight - window.innerHeight
    window.scrollTo({ top: Math.min(y, Math.max(0, room)), behavior: 'instant' as ScrollBehavior })
    if (room >= y || performance.now() - t0 > 2500) return
    requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}
// a scroll the person makes stops a restore that's still waiting for the page to fill in
for (const ev of ['wheel', 'touchstart', 'keydown']) window.addEventListener(ev, () => { restoring++ }, { passive: true })

window.addEventListener('popstate', (e) => {
  depth = Math.max(0, depth - 1)
  scrolls.set(key, window.scrollY)
  key = (e.state as { k?: string } | null)?.k || newKey()
  emit()
  restore(scrolls.get(key) || 0)
})

type Guard = (to: string) => boolean | Promise<boolean>
let guard: Guard | null = null
/* A screen with unsaved work can ask before the route changes (the map builder, a running game). */
export function setNavGuard(g: Guard | null) {
  guard = g
}

export async function navigate(to: string, opts: { replace?: boolean; force?: boolean } = {}) {
  if (!opts.force && guard) {
    const ok = await guard(to)
    if (!ok) return
  }
  const url = new URL(to, location.origin)
  if (url.pathname + url.search === location.pathname + location.search && !opts.replace) return
  scrolls.set(key, window.scrollY)
  if (opts.replace) history.replaceState({ k: key }, '', url.pathname + url.search + url.hash)
  else {
    key = newKey()
    history.pushState({ k: key }, '', url.pathname + url.search + url.hash)
    depth++
  }
  restoring++
  emit()
  if (!url.hash) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
}

export function back(fallback = '/') {
  if (depth > 0) history.back()
  else void navigate(fallback, { replace: true })
}

export function useRoute(): Route {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb) },
    () => current,
  )
}
export const getRoute = () => current

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  return (
    <a
      href={to}
      {...rest}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e)
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || rest.target === '_blank') return
        e.preventDefault()
        void navigate(to)
      }}
    />
  )
}
